import { Button, Carousel } from 'react-bootstrap';
import React, { useRef } from 'react';
import {
  dark,
  external,
  primaryGradientVertical,
  secondaryGradientVertical,
  solarGradientVertical,
  successGradientVertical,
  white,
} from '../theme/colors';
import {
  faChevronLeft,
  faShare,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

import Alexa from '../assets/svg/year-in-review/alexa.svg';
import Donated from '../assets/svg/year-in-review/donated.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullScreenLayout from '../ui/Layout/FullScreenLayout';
import Intro from '../assets/svg/year-in-review/intro.svg';
import Reduced from '../assets/svg/year-in-review/reduced.svg';
import Solar from '../assets/svg/year-in-review/solar.svg';
import Tdas from '../assets/svg/year-in-review/tdas.svg';
import Thanks from '../assets/svg/year-in-review/thanks.svg';
import styled from 'styled-components';

const StyledCarousel = styled(Carousel)`
  height: 100%;
  position: fixed;

  .carousel-indicators {
    position: fixed;
    bottom: 6px;

    li {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    @media only screen and (min-width: 360px) {
      bottom: 0;
    }
  }
`;

const StyledCarouselItem = styled(Carousel.Item)`
  height: 100%;
  position: fixed;
  justify-content: space-around;
  background: ${props => props.background};
`;

const StyledImage = styled.img`
  margin: auto;
  display: block;
  max-width: 100%;
  padding-left: 20px;
  padding-right: 20px;

  @media only screen and (max-width: 380px) {
    width: 280px;
  }
`;

const StyledCarouselCaption = styled(Carousel.Caption)`
  left: auto;
  right: auto;
  width: 100vw;
  height: 100%;
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-size: 32px;
`;

const StyledContentWrapper = styled.div`
  padding-bottom: 50px;

  @media only screen and (max-width: 380px) {
    font-size: 24px;
    padding-bottom: 0;
  }
`;

const StyledContent = styled.div`
  h1 {
    font-size: 32px;
    color: ${white};
    font-weight: regular;
    margin-bottom: 20px;

    @media only screen and (max-width: 380px) {
      font-size: 24px;
    }
  }
  p {
    font-size: 18px;

    @media only screen and (max-width: 380px) {
      font-size: 16px;
    }
  }
`;

const StyledCaptionSpacer = styled.div`
  max-width: 450px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 14px;
  padding-left: 10px;
  padding-right: 10px;
`;

const StyledButton = styled(Button)`
  background: #fff2;
  border: none;
  margin-bottom: 10px;
`;

const StyledHeaderDiv = styled.div`
  z-index: 10000;
  position: absolute;
  top: 0;
  width: 100vw;
  height: auto;
  display: flex;
  justify-content: space-between;
`;

const CarouselItemContent = ({
  content,
  buttonLabel = 'Next',
  secondaryLabel,
  onNext,
  onBack,
  onSecondary,
  image,
}) => (
  <div>
    <StyledHeaderDiv>
      <div>
        {!!onBack && (
          <a href="/" onClick={onBack}>
            <FontAwesomeIcon
              className="em-text p-4 mt-4 color-white"
              icon={faChevronLeft}
            />
          </a>
        )}
      </div>
      <div>
        <a href="https://loop.homes">
          <FontAwesomeIcon
            className="em-text p-4 mt-4 color-white"
            icon={faXmark}
          />
        </a>
      </div>
    </StyledHeaderDiv>
    <StyledCarouselCaption className="p-4">
      <div></div>
      <div className="d-flex justify-content-center align-center w-100">
        <StyledImage className="px-5 px-md-4" src={image} alt="Loop phone" />
      </div>
      <StyledCaptionSpacer>
        <StyledContentWrapper>{content}</StyledContentWrapper>
        {onNext && (
          <Button className="mb-0 w-100" onClick={onNext}>
            {buttonLabel}
          </Button>
        )}
        {secondaryLabel && onSecondary && (
          <StyledButton className="mb-0 mt-3 w-100" onClick={onSecondary}>
            {secondaryLabel}
          </StyledButton>
        )}
      </StyledCaptionSpacer>
    </StyledCarouselCaption>
  </div>
);

const onShare = async () => {
  const blob = await fetch('/images/loop-2023-year-in-review.png').then(r =>
    r.blob(),
  );
  const data = {
    files: [
      new File([blob], 'file.png', {
        type: blob.type,
      }),
    ],
    title: 'Loop 2023 Year In Review',
    text:
      'Checkout what the Loop community has achieved in 2023. ' +
      'Download Loop for free today and join the community for 2024! https://loop.homes',
  };
  try {
    if (!navigator.canShare(data)) {
      throw new Error("Can't share data.", data);
    }
    await navigator.share(data);
  } catch (err) {
    console.error(err.name, err.message);
  }
};

const IntroContent = (
  <StyledContent>
    <h1 className="normal">Welcome to Loop’s 2023 Year in Review</h1>
    <p>Let’s take a look back on 2023 and see what Loop users have achieved.</p>
  </StyledContent>
);

const YearSummaryContainer = () => {
  const ref = useRef(null);
  const onBack = e => {
    e.preventDefault();
    return ref.current.prev();
  };
  const onNext = e => {
    e.preventDefault();
    return ref.current.next();
  };
  return (
    <FullScreenLayout>
      <StyledCarousel interval={null} controls={false} ref={ref} wrap={false}>
        <StyledCarouselItem background={secondaryGradientVertical}>
          <CarouselItemContent
            image={Intro}
            content={IntroContent}
            buttonLabel="Start"
            onNext={onNext}
          />
        </StyledCarouselItem>
        <StyledCarouselItem background={primaryGradientVertical}>
          <CarouselItemContent
            image={Reduced}
            content="On average, Loop users have reduced their energy use by 15%"
            onNext={onNext}
            onBack={onBack}
          />
        </StyledCarouselItem>
        <StyledCarouselItem background={dark}>
          <CarouselItemContent
            image={Tdas}
            content="Loop customers earned over £100,000 through our Turn Down and Save scheme"
            onNext={onNext}
            onBack={onBack}
          />
        </StyledCarouselItem>
        <StyledCarouselItem background={successGradientVertical}>
          <CarouselItemContent
            image={Donated}
            content="Over £20,000 was donated by Loop users to the National Energy Action charity"
            onNext={onNext}
            onBack={onBack}
          />
        </StyledCarouselItem>
        <StyledCarouselItem background={solarGradientVertical}>
          <CarouselItemContent
            image={Solar}
            content="Loop Solar launched with our installation partners"
            onNext={onNext}
            onBack={onBack}
          />
        </StyledCarouselItem>
        <StyledCarouselItem background={external}>
          <CarouselItemContent
            image={Alexa}
            content="Loop’s Alexa Skill launched. 
“Alexa, how much energy did I use yesterday?”"
            onNext={onNext}
            onBack={onBack}
          />
        </StyledCarouselItem>
        <StyledCarouselItem background={secondaryGradientVertical}>
          <CarouselItemContent
            image={Thanks}
            content="Thanks for being a part of Loop in 2023! "
            buttonLabel={
              <>
                Share
                <FontAwesomeIcon className="ml-2 color-white" icon={faShare} />
              </>
            }
            secondaryLabel="Close"
            onNext={!!navigator.canShare ? onShare : undefined}
            onBack={onBack}
            onSecondary={() => window.location.assign('https://loop.homes')}
          />
        </StyledCarouselItem>
      </StyledCarousel>
    </FullScreenLayout>
  );
};

export default YearSummaryContainer;
